import { useState, useEffect, useRef } from 'react'
import { IoDocument } from "react-icons/io5";
import { FaXmark, FaFilePdf, FaFileExcel } from "react-icons/fa6";
import { HiArrowDownTray } from "react-icons/hi2";
import { toast } from 'react-toastify';
import HTSCard from '../comps/HTSCard'
import PartInfoCard from './PartInfoCard'
import IndividualMaterialSummary from './IndividualMaterialSummary'
import axios from 'axios';
import { useLocation, useHistory } from 'react-router-dom';
import TariffCalculator from './TariffCalculator'
import ThumbnailImage from './ThumbnailImage';
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import WeightResult from './WeightResult'
import CADExtractView from './CADExtractView'
import ImageZoomModal from './ImageZoomModal.jsx'
import PdfViewerModal from './PdfViewerModal.jsx'
import PartClassificationV2, { ExportButtons } from './tariffV2/PartClassificationV2.jsx'
import CalculatorV2 from './tariffV2/CalculatorV2.jsx'

// ---------------------------------------------------------------------------
// Drill-Down helpers
// ---------------------------------------------------------------------------

const DD_LOG_COLORS = {
  phase:   'text-[#f26522]',
  success: 'text-[#94c43d]',
  info:    'text-gray-400',
  warning: 'text-[#f26522]',
  error:   'text-red-400',
}

/** Markdown renderer with consistent inline styles for the drill-down panel */
function Md({ children }) {
  if (!children) return null
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p:          ({ node, ...p }) => <p          className="text-gray-700 mb-2 leading-relaxed text-sm last:mb-0" {...p} />,
        strong:     ({ node, ...p }) => <strong     className="font-semibold text-gray-900" {...p} />,
        em:         ({ node, ...p }) => <em         className="italic text-gray-700" {...p} />,
        code:       ({ node, className, children, ...p }) => {
          const isInline = !className && !String(children ?? '').includes('\n')
          return isInline
            ? <code className="bg-gray-100 text-indigo-700 px-1 py-0.5 rounded text-xs font-mono" {...p}>{children}</code>
            : <pre className="bg-gray-100 rounded p-2 text-xs font-mono overflow-x-auto my-2 whitespace-pre-wrap"><code className={className} {...p}>{children}</code></pre>
        },
        ul:         ({ node, ...p }) => <ul         className="list-disc list-inside space-y-0.5 mb-2 text-sm" {...p} />,
        ol:         ({ node, ...p }) => <ol         className="list-decimal list-inside space-y-0.5 mb-2 text-sm" {...p} />,
        li:         ({ node, ...p }) => <li         className="text-gray-700" {...p} />,
        blockquote: ({ node, ...p }) => <blockquote className="border-l-2 border-indigo-400 pl-3 italic text-gray-500 my-2 text-sm" {...p} />,
        table:      ({ node, ...p }) => <div className="overflow-x-auto my-2"><table className="text-xs border-collapse w-full" {...p} /></div>,
        th:         ({ node, ...p }) => <th         className="border border-gray-300 bg-gray-50 px-2 py-1 font-semibold text-left text-xs" {...p} />,
        td:         ({ node, ...p }) => <td         className="border border-gray-300 px-2 py-1 text-gray-700 text-xs" {...p} />,
      }}
    >
      {children}
    </ReactMarkdown>
  )
}

const DIFF_BADGE = {
  Easy:   'bg-green-100 text-green-700',
  Medium: 'bg-amber-100 text-amber-700',
  Hard:   'bg-red-100 text-red-700',
}

/** Fully formatted drill-down result panel */
function DrillDownPanel({ data }) {
  const [open, setOpen] = useState({
    analysis:  true,
    impact:    true,
    solutions: true,
    concerns:  false,
    rationale: false,
  })
  const toggle = key => setOpen(s => ({ ...s, [key]: !s[key] }))

  const Section = ({ id, label, borderColor, headerBg, headerText, children }) => (
    <div className={`border rounded-lg overflow-hidden mb-2 ${borderColor}`}>
      <button
        onClick={() => toggle(id)}
        className={`w-full flex items-center justify-between px-3 py-2 text-left ${headerBg}`}
      >
        <span className={`font-semibold text-xs uppercase tracking-wide ${headerText}`}>{label}</span>
        <span className={`text-xs ${headerText}`}>{open[id] ? '▲' : '▼'}</span>
      </button>
      {open[id] && <div className="px-3 py-3">{children}</div>}
    </div>
  )

  return (
    <div className="mt-3 text-sm">

      {/* Summary headline */}
      {data.summary_headline && (
        <div className="mb-3 px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium leading-snug">
          🔍 {data.summary_headline}
        </div>
      )}

      {/* Deep Analysis */}
      <Section
        id="analysis" label="Deep Analysis"
        borderColor="border-indigo-200" headerBg="bg-indigo-50" headerText="text-indigo-700"
      >
        <Md>{data.deep_analysis}</Md>
      </Section>

      {/* Impact Assessment */}
      {data.impact_assessment && (
        <Section
          id="impact" label="Impact Assessment"
          borderColor="border-blue-200" headerBg="bg-blue-50" headerText="text-blue-700"
        >
          <div className="grid grid-cols-2 gap-2">
            {[
              { key: 'manufacturing', label: 'Manufacturing', icon: '⚙️' },
              { key: 'assembly',      label: 'Assembly',      icon: '🔩' },
              { key: 'function',      label: 'Function',      icon: '📐' },
              { key: 'reliability',   label: 'Reliability',   icon: '🔬' },
            ].map(({ key, label, icon }) => (
              <div key={key} className="bg-gray-50 rounded p-2 border border-gray-200">
                <div className="text-xs font-semibold text-gray-500 mb-1">{icon} {label}</div>
                <Md>{data.impact_assessment[key]}</Md>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Solution Options */}
      {data.solution_options?.length > 0 && (
        <Section
          id="solutions" label={`Solution Options (${data.solution_options.length})`}
          borderColor="border-green-200" headerBg="bg-green-50" headerText="text-green-700"
        >
          <div className="space-y-3">
            {data.solution_options.map((sol, i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-3 bg-white">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900 text-sm">{sol.id}: {sol.title}</span>
                    {sol.am_relevant && (
                      <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs rounded font-medium">AM</span>
                    )}
                  </div>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium shrink-0 ${DIFF_BADGE[sol.difficulty] || 'bg-gray-100 text-gray-600'}`}>
                    {sol.difficulty}
                  </span>
                </div>
                <Md>{sol.description}</Md>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div>
                    <div className="text-xs font-semibold text-green-700 mb-1">Pros</div>
                    <ul className="space-y-0.5">
                      {(sol.pros || []).map((p, pi) => (
                        <li key={pi} className="text-xs text-gray-700 flex gap-1">
                          <span className="text-green-500 shrink-0">✓</span>
                          <Md>{p}</Md>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-red-600 mb-1">Cons</div>
                    <ul className="space-y-0.5">
                      {(sol.cons || []).map((c, ci) => (
                        <li key={ci} className="text-xs text-gray-700 flex gap-1">
                          <span className="text-red-400 shrink-0">✗</span>
                          <Md>{c}</Md>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                {sol.am_relevant && sol.am_notes && (
                  <div className="mt-2 px-2 py-1.5 bg-purple-50 border border-purple-200 rounded text-xs">
                    <span className="font-semibold text-purple-700">AM Notes: </span>
                    <Md>{sol.am_notes}</Md>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Related Concerns */}
      {data.related_concerns?.length > 0 && (
        <Section
          id="concerns" label={`Related Concerns (${data.related_concerns.length})`}
          borderColor="border-amber-200" headerBg="bg-amber-50" headerText="text-amber-700"
        >
          <div className="space-y-2">
            {data.related_concerns.map((c, i) => (
              <div key={i} className="flex gap-3 p-2 bg-amber-50 rounded border border-amber-100">
                <div className="shrink-0 text-amber-500 mt-0.5">⚠</div>
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 text-xs mb-0.5">{c.title}</div>
                  <Md>{c.description}</Md>
                  {c.check_location && (
                    <div className="text-xs text-gray-500 mt-1">
                      📍 Check: <span className="font-mono text-gray-700">{c.check_location}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Engineering Rationale + Recommended Action */}
      <Section
        id="rationale" label="Engineering Rationale"
        borderColor="border-gray-200" headerBg="bg-gray-50" headerText="text-gray-600"
      >
        <Md>{data.engineering_rationale}</Md>
        {data.recommended_action && (
          <div className="mt-3 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
            <div className="text-xs font-semibold text-green-700 mb-1">Recommended Action</div>
            <Md>{data.recommended_action}</Md>
          </div>
        )}
      </Section>

    </div>
  )
}

export default function FileDetailPanel({
  file,
  fileId,
  filename,
  onClose,
  defaultTab,
  isLoading,
  error,
}) {
  const location = useLocation()
  const history = useHistory()

  // Open the Report Builder prescoped to this file's space + project (WS2 / 4a redirect)
  const openReportBuilder = () => {
    const params = new URLSearchParams();
    if (space_path) params.set('space', space_path);
    if (folder_name) params.set('project', folder_name);
    history.push({
      pathname: '/reports',
      search: params.toString() ? `?${params.toString()}` : '',
      state: { state: { space_path, folder_name } },
    });
  };
  const { folder_name, space_path } = location.state?.state || {};
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [activeTab, setActiveTab] = useState(defaultTab || 'tariff')
  const [hasAutoSelected, setHasAutoSelected] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  // V2 Drawing Checker state
  const [feedbackState, setFeedbackState] = useState({}) // { [issueId]: 'interesting'|'not_interesting'|'neutral' }
  const [drillDownState, setDrillDownState] = useState({}) // { [issueId]: { loading, logs, data, error, collapsed } }
  const drillDownTimersRef = useRef({}) // { [issueId]: [timerId, ...] }
  const [annotationOverride, setAnnotationOverride] = useState(null) // { annotation_status, annotated_image_urls, annotated_thumbnail_base64 }
  const [annotatedZoomOpen, setAnnotatedZoomOpen] = useState(false)
  const [annotatedFullSrc, setAnnotatedFullSrc] = useState(null)
  const [annotatedFullLoading, setAnnotatedFullLoading] = useState(false)
  const [annotatedPage, setAnnotatedPage] = useState(1)
  const [annotatedFullError, setAnnotatedFullError] = useState(false)
  const annotatedFetchIdRef = useRef(0)
  const [reportModalOpen, setReportModalOpen] = useState(false)
  const [reportHtml, setReportHtml] = useState(null)
  const [reportLoading, setReportLoading] = useState(false)
  const [reportPdfViewer, setReportPdfViewer] = useState({ open: false, fileId: null, title: '' })

  const BASE_URL = process.env.REACT_APP_BE_MAIN_URL;
  const GETANALYSED_API = `${BASE_URL}/ocr/getanalysedfiles/`;
  const token = sessionStorage.getItem("userToken");

  // Debug logging
  console.log('=== FileDetail Component Loaded ===');
  console.log('process.env.REACT_APP_BE_MAIN_URL:', process.env.REACT_APP_BE_MAIN_URL);
  console.log('BASE_URL:', BASE_URL);
  console.log('GETANALYSED_API:', GETANALYSED_API);

  // Log for debugging
  console.log('FileDetail BASE_URL:', BASE_URL);

  const selectedFile = files.find(f => f.id === fileId);
  const fetchAnalysisData = async () => {
    try {
      setLoading(true);
      // Always include filename as fallback, add doc_id for precise lookup
      let url = GETANALYSED_API + `?filename=${encodeURIComponent(file.filename)}`;
      if (file._id) {
        // Add doc_id for precise document lookup
        url += `&doc_id=${encodeURIComponent(file._id)}`;
      }
      if (folder_name) {
        url += `&folder_name=${encodeURIComponent(folder_name)}`;
      }
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const allFiles = res.data.files || [];
      setFiles(allFiles);

      // If we queried by doc_id and got a result, use it directly
      if (file._id && allFiles.length === 1) {
        setData(allFiles[0]);
        return;
      }

      // Otherwise find the best matching document
      let matchedFile = allFiles[0]; // Default to first
      if (allFiles.length > 1) {
        // First try to match by folder_name
        if (folder_name) {
          const folderMatches = allFiles.filter(f => f.folder_name === folder_name);
          if (folderMatches.length === 1) {
            matchedFile = folderMatches[0];
          } else if (folderMatches.length > 1) {
            // Multiple matches in same folder - prefer one with more analysis types
            matchedFile = folderMatches.reduce((best, current) => {
              const bestCount = Object.keys(best.analyses || {}).length;
              const currentCount = Object.keys(current.analyses || {}).length;
              return currentCount > bestCount ? current : best;
            });
          }
        }
        // If no folder match, prefer document with most analysis types
        if (!matchedFile || matchedFile === allFiles[0]) {
          matchedFile = allFiles.reduce((best, current) => {
            const bestCount = Object.keys(best.analyses || {}).length;
            const currentCount = Object.keys(current.analyses || {}).length;
            return currentCount > bestCount ? current : best;
          });
        }
      }
      setData(matchedFile);

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle Excel export for individual file
  const handleExportExcel = async () => {
    if (!data?._id) {
      toast.error('No file data available for export');
      return;
    }

    setIsExporting(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/ocr/export/excel/individual/${data._id}/`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Get filename from response headers or generate one
      const contentDisposition = response.headers['content-disposition'];
      let downloadFilename = `${file.filename.replace(/\.[^/.]+$/, '')}_Tariff_Analysis.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) downloadFilename = match[1];
      }

      link.setAttribute('download', downloadFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Excel exported successfully');
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to export Excel file');
    } finally {
      setIsExporting(false);
    }
  };

  // Handle Excel export for Design Review
  const handleExportDesignReview = async () => {
    if (!data?._id) {
      toast.error('No file data available for export');
      return;
    }

    setIsExporting(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/ocr/export/excel/design-review/${data._id}/`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Get filename from response headers or generate one
      const contentDisposition = response.headers['content-disposition'];
      let downloadFilename = `${file.filename.replace(/\.[^/.]+$/, '')}_Design_Review.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) downloadFilename = match[1];
      }

      link.setAttribute('download', downloadFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Design Review exported successfully');
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to export Design Review Excel file');
    } finally {
      setIsExporting(false);
    }
  };

  // Handle Excel export for Drawing Checker
  const handleExportDrawingCheckerExcel = async (playType = 'veeco_drawing_checker_v2') => {
    if (!data?._id) {
      toast.error('No file data available for export');
      return;
    }

    if (!data?.analyses?.[playType]) {
      toast.error(`No ${playType} analysis found for this file.`);
      return;
    }

    setIsExporting(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/ocr/export/excel/drawing-checker/${data._id}/?play_type=${playType}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers['content-disposition'];
      let downloadFilename = `${file.filename.replace(/\.[^/.]+$/, '')}_Drawing_Checker.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) downloadFilename = match[1];
      }

      link.setAttribute('download', downloadFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Drawing Checker exported successfully');
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to export Drawing Checker Excel file');
    } finally {
      setIsExporting(false);
    }
  };

  // Fetch the full-resolution annotated drawing (from S3, via the authenticated
  // backend proxy) instead of using the compressed annotated_thumbnail_base64
  // preview — the thumbnail is intentionally low-res and isn't legible when zoomed.
  // Each page is fetched on its own (?page=N) — without it a multi-page
  // drawing comes back as a ZIP, and a pending annotation as 202 JSON;
  // neither renders in <img>.
  const openAnnotatedFullScreen = async (page = 1) => {
    if (!data?._id) return
    const fetchId = ++annotatedFetchIdRef.current
    setAnnotatedZoomOpen(true)
    setAnnotatedPage(page)
    setAnnotatedFullLoading(true)
    setAnnotatedFullError(false)
    try {
      const res = await axios.get(`${BASE_URL}/ocr/annotated-pdf/${data._id}/?page=${page}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      })
      if (!res.data?.type?.startsWith('image/')) throw new Error(`Unexpected response type: ${res.data?.type}`)
      if (fetchId !== annotatedFetchIdRef.current) return // user already moved to another page
      setAnnotatedFullSrc((prev) => {
        if (prev) URL.revokeObjectURL(prev)
        return URL.createObjectURL(res.data)
      })
    } catch (err) {
      if (fetchId !== annotatedFetchIdRef.current) return
      setAnnotatedFullSrc(null)
      setAnnotatedFullError(true)
    } finally {
      if (fetchId === annotatedFetchIdRef.current) setAnnotatedFullLoading(false)
    }
  }

  useEffect(() => {
    return () => { if (annotatedFullSrc) URL.revokeObjectURL(annotatedFullSrc) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annotatedFullSrc])

  // "View Original PDF" inside the Drawing Checker report iframe posts a
  // message to the parent instead of navigating away — open it in the app's
  // existing in-app PDF viewer (fetched authenticated, never a raw S3 link).
  //
  // "Download Page N" / "Download Annotated Image" also post a message here
  // instead of fetching+downloading inside the iframe — Chrome silently
  // blocks a.click()-triggered downloads originating from a srcDoc iframe
  // (blob is created, fetch succeeds, but nothing saves, no error). Doing
  // the fetch + download from this top-level page avoids that restriction.
  useEffect(() => {
    const handleMessage = (e) => {
      if (e.data?.type === 'SO_VIEW_ORIGINAL_PDF' && data?._id) {
        setReportPdfViewer({ open: true, fileId: data._id, title: filename })
      } else if (e.data?.type === 'SO_DOWNLOAD_IMAGE' && e.data?.url) {
        fetch(e.data.url)
          .then(response => {
            if (!response.ok) throw new Error('Download failed with status ' + response.status);
            return response.blob();
          })
          .then(blob => {
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = e.data.filename || 'download.png';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(downloadUrl);
          })
          .catch(err => {
            toast.error('Failed to download image: ' + err.message);
          });
      } else if (e.data?.type === 'SO_DOWNLOAD_DRAWING_CHECKER_PDF' && e.data?.fileId) {
        const playType = e.data.playType || 'veeco_drawing_checker_v2';
        axios.get(`${BASE_URL}/ocr/download/drawing-checker/${e.data.fileId}/?play_type=${encodeURIComponent(playType)}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        })
          .then(response => {
            const disposition = response.headers['content-disposition'] || '';
            const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
            const downloadName = match ? decodeURIComponent(match[1]) : (e.data.filename || 'Drawing_Checker_Report.pdf');
            const downloadUrl = window.URL.createObjectURL(response.data);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = downloadName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(downloadUrl);
          })
          .catch(err => {
            toast.error('Failed to download PDF report: ' + (err.response?.data?.error || err.message));
          });
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?._id])

  // Handle viewing Drawing Checker full report — fetched and rendered inside
  // an in-app modal (iframe), instead of opening a separate browser tab/window.
  // The report's own drill-down/feedback buttons keep working as-is: they
  // call fetch() with absolute /ocr/... URLs and a Bearer token (via
  // window.__authToken/__baseUrl, injected below), which works the same
  // whether the HTML is running in a real window or an iframe.
  const handleViewDrawingCheckerReport = async (playType = 'veeco_drawing_checker_v2') => {
    if (!data?._id) {
      toast.error('No file data available');
      return;
    }

    if (!data?.analyses?.[playType]) {
      toast.error(`No ${playType} analysis found for this file. Please run the analysis first.`);
      return;
    }

    const reportUrl = `${BASE_URL}/ocr/export/drawing-checker/${data._id}/?play_type=${playType}`;

    setReportModalOpen(true);
    setReportLoading(true);
    setReportHtml(null);

    try {
      const response = await axios.get(reportUrl, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'text'
      });

      // Fix relative URLs in the HTML to point to backend
      let htmlContent = response.data;
      htmlContent = htmlContent.replace(
        /href=["']\/ocr\//g,
        `href="${BASE_URL}/ocr/`
      );
      htmlContent = htmlContent.replace(
        /src=["']\/ocr\//g,
        `src="${BASE_URL}/ocr/`
      );

      // Inject JavaScript to handle authenticated downloads, and expose the
      // auth token/base URL the report's own drill-down/feedback buttons use.
      const authScript = `
        <script>
          (function() {
            const token = '${token}';
            const baseUrl = '${BASE_URL}';
            const playType = '${playType}';

            // Expose token and baseUrl for feedback/drill-down buttons
            window.__authToken = token;
            window.__baseUrl = baseUrl;

            // Intercept "View Original PDF" so it opens the app's in-app PDF
            // viewer instead of navigating this iframe/tab to a raw S3 link.
            document.addEventListener('click', function(e) {
              const pdfLink = e.target.closest('a.btn-secondary[target="_blank"]');
              if (pdfLink && pdfLink.textContent.trim() === 'View Original PDF') {
                e.preventDefault();
                window.parent.postMessage({ type: 'SO_VIEW_ORIGINAL_PDF' }, '*');
              }
            });

            // Intercept "Download Page N" / "Download Annotated Image" links —
            // these point at presigned S3 URLs, and the browser ignores the
            // HTML "download" attribute for cross-origin links. Fetching and
            // triggering a.click() from inside this srcDoc iframe is silently
            // blocked by Chrome (no error, blob is created, nothing saves) —
            // so hand the URL off to the parent page instead, which performs
            // the actual fetch + download outside the iframe's restricted context.
            document.addEventListener('click', function(e) {
              const link = e.target.closest('a[download]:not([href*="/ocr/download/"])');
              if (link) {
                e.preventDefault();
                const filename = link.textContent.trim().replace(/\\s+/g, '_') + '.png';
                window.parent.postMessage({ type: 'SO_DOWNLOAD_IMAGE', url: link.href, filename: filename }, '*');
              }
            });

            // Intercept clicks on download links
            document.addEventListener('click', function(e) {
              const link = e.target.closest('a[href*="/ocr/download/"]');
              if (link) {
                e.preventDefault();
                let url = link.href;

                if (!url.includes('play_type=')) {
                  const separator = url.includes('?') ? '&' : '?';
                  url = url + separator + 'play_type=' + encodeURIComponent(playType);
                }

                fetch(url, {
                  headers: { 'Authorization': 'Bearer ' + token }
                })
                .then(response => {
                  if (!response.ok) {
                    return response.text().then(text => {
                      try {
                        const errorData = JSON.parse(text);
                        throw new Error(errorData.error || errorData.detail || 'Download failed');
                      } catch (e) {
                        throw new Error(text || 'Download failed with status ' + response.status);
                      }
                    });
                  }

                  const disposition = response.headers.get('Content-Disposition');
                  let filename = 'Drawing_Checker_Report.pdf';
                  if (disposition && disposition.includes('filename=')) {
                    const matches = disposition.match(/filename="?(.+?)"?$/);
                    if (matches && matches[1]) {
                      filename = matches[1];
                    }
                  }

                  return response.blob().then(blob => ({ blob, filename }));
                })
                .then(({ blob, filename }) => {
                  const downloadUrl = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = downloadUrl;
                  a.download = filename;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  window.URL.revokeObjectURL(downloadUrl);
                })
                .catch(err => {
                  alert('Failed to download report: ' + err.message);
                });
              }
            });
          })();
        </script>
      `;

      htmlContent = htmlContent.replace('</body>', authScript + '</body>');
      setReportHtml(htmlContent);
    } catch (err) {
      let errorMessage = 'Failed to load Drawing Checker report';
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        } else if (err.response.data.detail) {
          errorMessage = err.response.data.detail;
        }
      }
      toast.error(errorMessage);
      setReportModalOpen(false);
    } finally {
      setReportLoading(false);
    }
  };

  // Handle downloading Drawing Checker report
  const handleDownloadDrawingCheckerReport = async (playType = 'veeco_drawing_checker_v2') => {
    if (!data?._id) {
      toast.error('No file data available for export');
      return;
    }

    // Log available analyses for debugging
    console.log('=== Download Drawing Checker Report Debug ===');
    console.log('Available analyses:', Object.keys(data?.analyses || {}));
    console.log('Requested play type:', playType);
    console.log('Analysis exists?', !!data?.analyses?.[playType]);
    console.log('File ID:', data._id);

    // Check if analysis exists for this play type
    if (!data?.analyses?.[playType]) {
      toast.error(`No ${playType} analysis found for this file. Please run the analysis first.`);
      return;
    }

    setIsExporting(true);

    const downloadUrl = `${BASE_URL}/ocr/download/drawing-checker/${data._id}/?play_type=${playType}`;
    console.log('Download Report - Full URL:', downloadUrl);
    console.log('Download Report - BASE_URL:', BASE_URL);
    console.log('Download Report - Play Type:', playType);

    try {
      const response = await axios.get(downloadUrl, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      console.log('Download Report - Response received');

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Get filename from response headers or generate one
      const contentDisposition = response.headers['content-disposition'];
      let downloadFilename = `${file.filename.replace(/\.[^/.]+$/, '')}_Drawing_Checker_Report.pdf`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) downloadFilename = match[1];
      }

      link.setAttribute('download', downloadFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Drawing Checker report downloaded successfully');
    } catch (err) {
      console.error('Download failed:', err);
      console.error('Error details:', err.response?.status, err.response?.data);
      console.error('Request URL:', downloadUrl);
      console.error('Play type sent:', playType);
      
      // Show more specific error message
      let errorMessage = 'Failed to download Drawing Checker report';
      if (err.response?.data) {
        // Handle blob error response
        if (err.response.data instanceof Blob) {
          // Convert blob to text to read error message
          err.response.data.text().then(text => {
            try {
              const errorData = JSON.parse(text);
              const detailedError = errorData.error || errorData.detail || text;
              toast.error(`Backend Error: ${detailedError}\n\nRequested: ${playType}\nThis appears to be a backend issue.`);
            } catch {
              toast.error(`Backend Error: ${text || errorMessage}\n\nRequested: ${playType}`);
            }
          });
          return;
        } else if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        } else if (err.response.data.detail) {
          errorMessage = err.response.data.detail;
        }
      }
      
      errorMessage = `Backend Error: ${errorMessage}\n\nRequested play type: ${playType}\nThis might be a backend issue with the play_type parameter.`;
      toast.error(errorMessage);
    } finally {
      setIsExporting(false);
    }
  };

  const tariffData = data?.analyses?.tariff_v1
  const tariffV2Data = data?.analyses?.tariff_v2
  const drawingCheckerV2Data = data?.analyses?.veeco_drawing_checker_v2
  const cadExtractData = data?.analyses?.cad_extract_v1

  // tariff_v2 gets its own "Tariff v2" tab; an older tariff_v1 result on the
  // same file stays readable under "Tariff Analysis".
  const hasTariffV2 = !!tariffV2Data?.data
  const tabs = [
    { id: 'tariff_v2', label: 'Tariff v2', available: hasTariffV2 },
    { id: 'tariff', label: 'Tariff Analysis', available: !!tariffData },
    { id: 'drawing_checker_v2', label: 'Drawing Checker V2', available: !!drawingCheckerV2Data },
    { id: 'cad_extract', label: 'Drawing Atlas', available: !!cadExtractData },
    { id: 'calculator', label: 'Calculator', available: !!tariffData || !!tariffV2Data?.data?.classification },
  ]

  useEffect(() => {
    if (!file) return
  }, [file])

  // Auto-select the first available tab when data loads
  useEffect(() => {
    if (!isLoading && !hasAutoSelected && data) {
      const currentTabAvailable = tabs.find(t => t.id === activeTab)?.available

      if (activeTab === 'tariff' && hasTariffV2) {
        setActiveTab('tariff_v2')   // the current tariff result opens first
      } else if (!currentTabAvailable) {
        const firstAvailable = tabs.find(t => t.available)
        if (firstAvailable) {
          setActiveTab(firstAvailable.id)
        }
      }
      setHasAutoSelected(true)
    }
  }, [isLoading, data, hasAutoSelected, activeTab, tabs])

  useEffect(() => {
    fetchAnalysisData();
  }, [filename]);

  // Poll for background annotation completion
  useEffect(() => {
    const annotationStatus = drawingCheckerV2Data?.data?.annotation_beta?.annotation_status
    if (annotationStatus !== 'pending') return

    const docId = file?._id
    if (!docId) return

    let cancelled = false
    const startTime = Date.now()
    const MAX_POLL_MS = 10 * 60 * 1000 // 10 minutes

    const poll = setInterval(async () => {
      if (cancelled) return
      if (Date.now() - startTime > MAX_POLL_MS) {
        clearInterval(poll)
        setAnnotationOverride({ annotation_status: 'failed', annotated_image_urls: [], annotated_thumbnail_base64: '' })
        return
      }
      try {
        const res = await axios.get(
          `${BASE_URL}/ocr/annotation-status/${docId}/?play_type=veeco_drawing_checker_v2`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        // 'unknown' means the backend has no annotation record at all for this
        // play, so no task is going to fill it in — stop polling instead of
        // spinning until the 10-minute timeout.
        if (['complete', 'failed', 'unknown'].includes(res.data.annotation_status)) {
          setAnnotationOverride(res.data)
          clearInterval(poll)
        }
      } catch (err) {
        console.warn('Annotation status poll failed:', err)
      }
    }, 5000)

    return () => { cancelled = true; clearInterval(poll) }
  }, [drawingCheckerV2Data?.data?.annotation_beta?.annotation_status, file?._id])

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-end z-50">
      <div className="h-full w-full max-w-3xl bg-white shadow-xl overflow-hidden flex flex-col">
        <div className="flex items-center gap-4 px-6 py-4 border-b bg-green-800 text-white">
          {file?.base_metadata?.s3_url && (
            <div className="w-20 h-20 border-2 rounded-md overflow-hidden">
              <ThumbnailImage
                fileId={file._id}
                filename={filename}
                s3Url={file.base_metadata.s3_url}
              />
            </div>
          )}

          <div className="flex-1 min-w-0 ">
            <div className="flex items-center gap-2">
              <h2 className="text-lg text-white font-semibold truncate">{file.filename}</h2>
              {/* WS5: duplicate-of pill */}
              {data?.duplicate_of && (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800 flex-shrink-0"
                  title={`First uploaded by ${data.duplicate_of.original_uploaded_by || 'someone'}${
                    data.duplicate_of.original_uploaded_at
                      ? ' on ' + data.duplicate_of.original_uploaded_at
                      : ''
                  }`}
                >
                  Already uploaded
                </span>
              )}
            </div>
            <p className="text-lg text-white font-semibold">Analysis completed
              {/* {data?.status === 'analyzed' ? 'Analysis Complete' : 'Pending Analysis'} */}
            </p>
          </div>

          {/* Tariff v2 exports: PDF report and Excel */}
          {(activeTab === 'tariff_v2' && hasTariffV2) && (
            <ExportButtons url={`${BASE_URL}/ocr/tariff-v2/export/part/${data?._id}/`} name="tariff_analysis" onDark />
          )}

          {/* Export button - show for tariff tab */}
          {(activeTab === 'tariff' && tariffData) && (
            <div className="flex flex-col items-end gap-1">
              <button
                onClick={() => {
                    handleExportExcel();
                }}
                disabled={isExporting}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50"
                title="Export to Excel"
              >
                {isExporting ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <HiArrowDownTray className="w-6 h-6" />
                )}
              </button>
              {/* 4a redirect: multi-file needs → Report Builder */}
              <button
                onClick={openReportBuilder}
                className="text-xs text-white/80 hover:text-white underline whitespace-nowrap"
                title="Build a report across many files"
              >
                Need one report across many files? Open the Report Builder →
              </button>
            </div>
          )}

          {/* Drawing Checker export buttons - PDF and Excel */}
          {(activeTab === 'drawing_checker_v2' && drawingCheckerV2Data) && (
            <>
              <button
                onClick={() => {
                  handleDownloadDrawingCheckerReport('veeco_drawing_checker_v2');
                }}
                disabled={isExporting}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50"
                title="Download Drawing Checker PDF Report"
              >
                {isExporting ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <FaFilePdf className="w-6 h-6" />
                )}
              </button>
              <button
                onClick={() => {
                  handleExportDrawingCheckerExcel('veeco_drawing_checker_v2');
                }}
                disabled={isExporting}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50"
                title="Export Drawing Checker to Excel"
              >
                {isExporting ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <FaFileExcel className="w-6 h-6" />
                )}
              </button>
            </>
          )}

          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <FaXmark className="w-6 h-6" />
          </button>
        </div>
        <div className="flex flex-wrap border-b">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => tab.available && setActiveTab(tab.id)}
              disabled={!tab.available}
              className={`px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${activeTab === tab.id
                ? 'text-black border-b-2 border-green-800'
                : tab.available
                  ? 'text-black hover:text-gray-700'
                  : 'text-gray-300 cursor-not-allowed'
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-gray-500">Loading analysis...</div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              Failed to load analysis data
            </div>
          ) : (
            <>
              {/* Tariff v2 Tab */}
              {activeTab === 'tariff_v2' && tariffV2Data?.data && (
                <PartClassificationV2 stored={tariffV2Data} />
              )}

              {/* Calculator Tab — tariff_v2 when the file has a v2 classification */}
              {activeTab === 'calculator' && tariffV2Data?.data?.classification && (
                <CalculatorV2 primary={tariffV2Data.data.classification.primary} />
              )}

              {/* Tariff Tab */}
              {activeTab === 'tariff' && tariffData?.data && (
                <div className="space-y-6">

                  {tariffData.data.tradeCompliance?.primaryHts && (
                    <HTSCard
                      primaryHts={tariffData.data.tradeCompliance.primaryHts}
                      alternativeHts={tariffData.data.tradeCompliance.alternativeHts}
                    />
                  )}

                  {/* Material Summary with Sec 232/301 indicators */}
                  {tariffData.data.materialSpecifications && (
                    <IndividualMaterialSummary
                      material={tariffData.data.materialSpecifications.material}
                      materialParent={tariffData.data.materialSpecifications.materialParent}
                      weightKg={tariffData.data.partInformation?.weightKg}
                      weightStatus={tariffData.data.partInformation?.weightStatus}
                      sec232={tariffData.data.tradeCompliance?.primaryHts?.sec232}
                      sec301={tariffData.data.tradeCompliance?.primaryHts?.sec301China}
                    />
                  )}

                  <PartInfoCard
                    partInfo={tariffData.data.partInformation || {}}
                    materialSpecs={tariffData.data.materialSpecifications}
                  />
                </div>
              )}
              {/* Calculator Tab */}
              {activeTab === 'calculator' && tariffData?.data && !tariffV2Data?.data?.classification && (
                <TariffCalculator
                  fileId={data?._id}
                  initialValues={{
                    htsCode: tariffData.data.tradeCompliance?.primaryHts?.code || '',
                    generalDutyPercent: tariffData.data.tradeCompliance?.primaryHts?.generalDutyRatePercent || 0,
                    material: tariffData.data.materialSpecifications?.material || '',
                    materialParent: tariffData.data.materialSpecifications?.materialParent || '',
                    sec232Info: tariffData.data.tradeCompliance?.primaryHts?.sec232,
                    sec301Info: tariffData.data.tradeCompliance?.primaryHts?.sec301China,
                  }}
                />
              )}

              {/* Drawing Checker V2 Tab */}
              {activeTab === 'drawing_checker_v2' && drawingCheckerV2Data?.data && (() => {
                const dcData = drawingCheckerV2Data.data
                const summary = dcData.summary || {}
                const topRisks = dcData.top_risks || []
                const annotation = annotationOverride || dcData.annotation_beta || {}
                const annotationStatus = annotation.annotation_status || 'complete'
                const thumbnail = annotation.annotated_thumbnail_base64
                const annotatedPageCount = Math.max(1, annotation.annotated_image_urls?.length || 0)

                const SEVERITY_COLORS = {
                  CRITICAL: 'bg-red-100 text-red-700',
                  MAJOR: 'bg-orange-100 text-orange-700',
                  MINOR: 'bg-amber-100 text-amber-700',
                  INFO: 'bg-gray-100 text-gray-600',
                }

                return (
                  <div className="space-y-6">
                    {/* Quality Summary */}
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                      <div className="bg-white border rounded-lg p-3 text-center">
                        <div className="text-2xl font-semibold text-[#1e5045]">
                          {summary.quality_score ?? '-'}/{summary.quality_max ?? 10}
                        </div>
                        <div className="text-xs text-gray-500">Quality Score</div>
                      </div>
                      <div className="bg-white border rounded-lg p-3 text-center">
                        <div className="text-2xl font-semibold text-gray-800">{summary.total_issues ?? 0}</div>
                        <div className="text-xs text-gray-500">Total Issues</div>
                      </div>
                      <div className="bg-red-50 border rounded-lg p-3 text-center">
                        <div className="text-2xl font-semibold text-red-700">{summary.critical ?? 0}</div>
                        <div className="text-xs text-gray-500">Critical</div>
                      </div>
                      <div className="bg-orange-50 border rounded-lg p-3 text-center">
                        <div className="text-2xl font-semibold text-orange-700">{summary.major ?? 0}</div>
                        <div className="text-xs text-gray-500">Major</div>
                      </div>
                      <div className="bg-amber-50 border rounded-lg p-3 text-center">
                        <div className="text-2xl font-semibold text-amber-700">{summary.minor ?? 0}</div>
                        <div className="text-xs text-gray-500">Minor</div>
                      </div>
                    </div>

                    {/* Annotated Drawing Preview */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                        Annotated Drawing
                        {thumbnail && (
                          <span className="text-[10px] text-gray-400 font-normal">Click to view full screen</span>
                        )}
                      </h4>
                      {thumbnail ? (
                        <>
                          <div
                            className="relative group cursor-pointer rounded-lg overflow-hidden border"
                            onClick={() => openAnnotatedFullScreen(1)}
                          >
                            <img
                              src={thumbnail}
                              alt="Annotated drawing preview"
                              className="max-w-full"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                          </div>
                          {/* The thumbnail is page 1 only, so it's a valid fallback only there. */}
                          <ImageZoomModal
                            open={annotatedZoomOpen}
                            onClose={() => setAnnotatedZoomOpen(false)}
                            src={annotatedFullLoading ? null : (annotatedFullSrc || (annotatedPage === 1 ? thumbnail : null))}
                            fallbackSrc={annotatedPage === 1 ? thumbnail : undefined}
                            title="Annotated Drawing"
                            resolutionLabel={annotatedFullLoading ? 'Loading full resolution...' : (annotatedFullSrc ? undefined : (annotatedPage === 1 ? 'Preview quality — full image unavailable' : undefined))}
                            errorText={!annotatedFullLoading && annotatedFullError && annotatedPage !== 1 ? `Could not load page ${annotatedPage}. Try again or download the annotated images.` : undefined}
                            page={annotatedPage}
                            pageCount={annotatedPageCount}
                            onPageChange={(p) => openAnnotatedFullScreen(p)}
                          />
                        </>
                      ) : (annotationStatus === 'failed' || annotationStatus === 'unknown') ? (
                        <div className="bg-gray-50 border rounded-lg p-4 text-sm text-gray-500">
                          {annotationStatus === 'unknown'
                            ? 'No annotated preview is available for this drawing. The issue list below is still complete — re-run the play to generate one, or open the full report or PDF for details.'
                            : 'Annotation generation failed for this drawing. The issue list below is still complete — open the full report or PDF for details.'}
                        </div>
                      ) : (
                        <div className="bg-gray-50 border rounded-lg p-4 text-sm text-gray-500 flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-[#1e5045] border-t-transparent rounded-full animate-spin" />
                          Annotated preview is still being generated in the background...
                        </div>
                      )}
                    </div>

                    {/* Top Risks */}
                    {topRisks.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Top Risks</h4>
                        <div className="space-y-2">
                          {topRisks.map((risk, idx) => (
                            <div key={risk.id || idx} className="flex items-start gap-2 bg-white border rounded-lg p-3">
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${SEVERITY_COLORS[risk.severity] || 'bg-gray-100 text-gray-600'}`}>
                                {risk.severity || 'INFO'}
                              </span>
                              <span className="text-sm text-gray-700">{risk.title}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <button
                      onClick={() => handleViewDrawingCheckerReport('veeco_drawing_checker_v2')}
                      className="px-4 py-2 bg-[#1e5045] text-white rounded-lg text-sm font-medium hover:bg-[#0d5d57] transition-colors"
                    >
                      View Full Report
                    </button>
                  </div>
                )
              })()}

              {/* Drawing Atlas Tab */}
              {activeTab === 'cad_extract' && (
                <CADExtractView
                  data={cadExtractData?.data}
                  success={cadExtractData?.success !== false}
                  error={cadExtractData?.error}
                  metadata={cadExtractData?.metadata}
                  fileId={file._id}
                />
              )}

              {/* Loading state — the results fetch takes a few seconds, and
                  without this the "No Analysis Data" empty state below shows
                  in the meantime, telling users their completed analysis
                  produced nothing. */}
              {loading && !tariffData && !hasTariffV2 && !drawingCheckerV2Data && !cadExtractData && (
                <div className="text-center py-12">
                  <div
                    className="w-10 h-10 mx-auto rounded-full border-4 border-gray-200 border-t-[#1e5045] animate-spin"
                    role="status"
                    aria-label="Loading analysis results"
                  />
                  <h3 className="mt-4 text-lg font-medium text-gray-900">
                    Loading analysis results…
                  </h3>
                  <p className="mt-2 text-gray-500">
                    Fetching the results for this file
                  </p>
                </div>
              )}

              {/* No data state — only once the fetch has actually finished */}
              {!loading && !tariffData && !hasTariffV2 && !drawingCheckerV2Data && !cadExtractData && (
                <div className="text-center py-12">
                  <IoDocument className="w-12 h-12 text-gray-300 mx-auto" />
                  <h3 className="mt-4 text-lg font-medium text-gray-900">
                    No Analysis Data
                  </h3>
                  <p className="mt-2 text-gray-500">
                    Run an analysis on this file to see results
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Drawing Checker Full Report — rendered in-app via an iframe instead
          of opening in a new browser tab. The report's own drill-down and
          feedback buttons keep working unchanged (see handleViewDrawingCheckerReport). */}
      {reportModalOpen && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full h-full max-w-6xl overflow-hidden flex flex-col">
            <div className="px-4 py-3 bg-[#1e5045] text-white flex items-center justify-between flex-shrink-0">
              <span className="font-medium">Drawing Checker Report</span>
              <button
                onClick={() => setReportModalOpen(false)}
                className="hover:bg-white/10 rounded p-1.5 transition-colors"
              >
                <FaXmark size={18} />
              </button>
            </div>
            <div className="flex-1 min-h-0 bg-gray-100 flex items-center justify-center">
              {reportLoading && (
                <div className="flex flex-col items-center gap-3 text-gray-500">
                  <div className="w-7 h-7 border-2 border-[#1e5045] border-t-transparent rounded-full animate-spin" />
                  <span>Loading report...</span>
                </div>
              )}
              {!reportLoading && reportHtml && (
                <iframe
                  title="Drawing Checker Report"
                  srcDoc={reportHtml}
                  className="w-full h-full border-0 bg-white"
                />
              )}
            </div>
          </div>
        </div>
      )}

      <PdfViewerModal
        open={reportPdfViewer.open}
        fileId={reportPdfViewer.fileId}
        title={reportPdfViewer.title}
        onClose={() => setReportPdfViewer({ open: false, fileId: null, title: '' })}
      />
    </div>
  )
}




